// Shared browser preference. Store variant IDs only, never an order or identity.
window.AveyShadeMemory = (() => {
  const prefix = 'avey:shade-memory:v1:';
  const maxAge = 180 * 24 * 60 * 60 * 1000;
  let privacyReady;
  const allowed = () => window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() === true;
  const keyFor = (productId) => /^\d+$/.test(String(productId)) ? `${prefix}${productId}` : null;
  const normalize = (ids) => Array.isArray(ids)
    ? [...new Set(ids.map(String))].filter((id) => /^\d+$/.test(id)).slice(0, 10)
    : [];

  return Object.freeze({
    ready() {
      if (allowed() || window.Shopify?.customerPrivacy) return Promise.resolve();
      if (!privacyReady) {
        privacyReady = new Promise((resolve) => {
          if (!window.Shopify?.loadFeatures) return resolve();
          window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], resolve);
        });
      }
      return privacyReady;
    },
    allowed,
    read(productId) {
      const key = keyFor(productId);
      if (!key || !allowed()) return [];
      try {
        const value = JSON.parse(window.localStorage.getItem(key));
        const age = Date.now() - value?.savedAt;
        if (value?.version !== 1 || !Number.isFinite(age) || age < 0 || age > maxAge) {
          window.localStorage.removeItem(key);
          return [];
        }
        return normalize(value.variantIds);
      } catch (_error) {
        return [];
      }
    },
    remember(productId, variantIds) {
      const key = keyFor(productId);
      const ids = normalize(variantIds);
      if (!key || !ids.length || !allowed()) return false;
      try {
        window.localStorage.setItem(key, JSON.stringify({ version: 1, variantIds: ids, savedAt: Date.now() }));
        return true;
      } catch (_error) {
        // Browsers can disable storage; the ordinary shade selector still works.
        return false;
      }
    },
    forget(productId) {
      const key = keyFor(productId);
      if (!key) return;
      try { window.localStorage.removeItem(key); } catch (_error) { /* Storage unavailable. */ }
    },
  });
})();

class AveyProductShadeMemory extends HTMLElement {
  async connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.interacted = false;
    this.restoring = false;
    this.automaticSelection = false;
    this.product = document.querySelector('avey-product');
    if (!this.product) return;
    this.product.addEventListener('change', this.onChange);
    document.addEventListener('visitorConsentCollected', this.onConsentChange);
    await customElements.whenDefined('avey-product');
    if (!this.isConnected) return;
    if (this.dataset.connected !== 'true') await window.AveyShadeMemory.ready();
    if (this.isConnected) this.onConsentChange();
  }

  disconnectedCallback() {
    this.product?.removeEventListener('change', this.onChange);
    document.removeEventListener('visitorConsentCollected', this.onConsentChange);
    this.setNotice();
    this.initialized = false;
  }

  setNotice(text = '') {
    for (const notice of this.product?.querySelectorAll('[data-shade-memory-notice]') || []) {
      if (notice.textContent !== text) notice.textContent = text;
      notice.hidden = !text;
    }
  }

  preserveAutomaticUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('variant')) return;
    url.searchParams.delete('variant');
    window.history.replaceState(window.history.state, '', url);
  }

  restore() {
    const connected = this.dataset.connected === 'true';
    if (!connected && !window.AveyShadeMemory.allowed()) {
      this.dataset.state = 'consent-required';
      return;
    }
    if (this.interacted || this.dataset.explicitVariant === 'true') return;
    const orderNode = connected ? document.querySelector('[data-product-order-shades]') : null;
    const ids = connected
      ? [...new Set(JSON.parse(orderNode?.textContent || '[]'))]
      : window.AveyShadeMemory.read(this.dataset.productId);
    // Several previously chosen shades are ambiguous: keep the explicit choice.
    if (ids.length !== 1) {
      this.dataset.state = 'ready';
      return;
    }
    let variant = this.product.variantData?.find((item) => String(item.id) === ids[0] && item.available);
    if (!variant) return;
    const bundle = this.product.querySelector('avey-bundle-selector');
    const fields = [...this.product.querySelectorAll('[data-bundle-shade], [data-product-shade]')];
    if (!fields.length) return;
    if (!bundle) {
      const remembered = variant;
      variant = this.product.variantData.find((item) => item.available && this.product.optionSelectors.every((field, index) =>
        item.options[index] === (field.hasAttribute('data-product-shade') ? remembered.options[index] : field.value)
      ));
      if (!variant) return;
    }
    this.restoring = true;
    fields.forEach((field) => {
      const value = field.hasAttribute('data-bundle-shade')
        ? String(variant.id) : variant.options[Number(field.dataset.optionPosition) - 1];
      const option = [...field.options].find((item) => item.value === value && !item.disabled);
      if (!option) return;
      field.value = value;
      window.AveyGlobalShadePicker?.syncTrigger(field);
    });
    if (bundle) bundle.updateSelection({ announce: false });
    else this.product.onOptionChange();
    this.restoring = false;
    this.dataset.state = 'restored';
    this.dataset.restoredVariant = String(variant.id);
    const applied = fields.every((field) => field.value === (field.hasAttribute('data-bundle-shade')
      ? String(variant.id) : variant.options[Number(field.dataset.optionPosition) - 1]));
    const source = connected ? (orderNode?.dataset?.preferenceSource === 'favorite' ? 'favorite' : 'order') : 'remembered';
    this.automaticSelection = applied;
    if (applied) this.preserveAutomaticUrl();
    this.setNotice(applied ? this.dataset[`${source}Notice`] || '' : '');
  }

  onChange = (event) => {
    if (this.restoring) return;
    const field = event.target;
    if (!field.matches('[data-bundle-radio], [data-bundle-shade], [data-option-position]')) return;
    this.interacted = true;
    if (field.matches('[data-bundle-shade], [data-product-shade]')) {
      this.automaticSelection = false;
      this.setNotice();
    } else if (field.matches('[data-bundle-radio]') && this.automaticSelection) this.preserveAutomaticUrl();
    if (this.dataset.connected === 'true') return;
    if (!field.matches('[data-bundle-shade], [data-product-shade]')) return;
    const bundle = this.product.querySelector('avey-bundle-selector');
    const ids = bundle ? bundle.selectedVariantIds : this.product.variantData?.filter((variant) =>
      variant.options.every((option, index) => option === this.product.optionSelectors[index]?.value)
    ).map((variant) => String(variant.id));
    const availableIds = ids?.filter((id) => this.product.variantData?.some(
      (variant) => String(variant.id) === String(id) && variant.available,
    ));
    if (!availableIds?.length) return;
    this.pendingIds = availableIds;
    this.remember();
  };

  remember() {
    const saved = window.AveyShadeMemory.remember(this.dataset.productId, this.pendingIds);
    this.dataset.state = saved ? 'remembered'
      : window.AveyShadeMemory.allowed() ? 'storage-unavailable' : 'consent-required';
  }

  onConsentChange = () => {
    if (this.dataset.connected === 'true') {
      if (!this.interacted) this.restore();
      return;
    }
    if (!window.AveyShadeMemory.allowed()) {
      window.AveyShadeMemory.forget(this.dataset.productId);
      this.dataset.state = 'consent-required';
      this.automaticSelection = false;
      this.setNotice();
    } else if (this.pendingIds?.length) {
      this.remember();
    } else if (!this.interacted) {
      this.restore();
    }
  };
}

if (!customElements.get('avey-product-shade-memory')) {
  customElements.define('avey-product-shade-memory', AveyProductShadeMemory);
}
